import { notFound, redirect } from 'next/navigation';

import {
  getAsset,
  getChannel,
  getContent,
  getDocument,
  getPlaceId,
  hasDuplicates,
  prependToNonHttpsHrefs,
  replaceDynamicData,
} from '@lib/server';
import type { Aggregator, Place } from '@types';
import { NestedRepeaterLink, RepeaterLink, VenueBookingType } from '@types';

import { getOrganisation } from '../get-organisation';

export async function getVenueLayoutData(domain: string, venue: string) {
  const channel = await getChannel(domain);
  const org = await getOrganisation();
  const operatorLogo = (await getAsset(org?.logo?.[0]?.id)) || undefined;

  if (!channel?.channelId) {
    notFound();
  }

  if (channel?.places?.length <= 1) redirect('/');

  const { fields, id } = await getDocument<Aggregator>('aggregator', channel.micrositeData[0].id, {
    select: [
      'fields.id',
      'fields.brandLogo',
      'fields.backgroundImage',
      'fields.backgroundImageOpacity',
      'fields.bookingTypes',
      'fields.venuesPageSlug',
      'fields.name',
    ],
    depth: 0,
    tags: [channel.channelId],
  });

  if (!fields || !id) {
    notFound();
  }

  const venuesPageSlug = fields.venuesPageSlug || 'bars';

  const { placeId } = await getPlaceId(channel, venue);

  if (!placeId) notFound();

  const place = await getDocument<Place>('place', placeId, {
    tags: [channel.channelId],
    depth: 1,
    visibilityId: placeId,
    select: [
      'fields.default',
      'fields.custom.navigationReference',
      'fields.custom.homepageTemplate',
      'fields.custom.displayName',
      'fields.custom.venueAnnouncements',
      'fields.custom.venueFooter',
      'fields.name',
      'id',
    ],
  });

  const dynamicPlace = replaceDynamicData({
    ...place?.fields,
    bookingTypes: fields.bookingTypes,
  });

  // Filter and format navigation links
  const navigationReference = (
    dynamicPlace?.custom?.navigationReference && dynamicPlace?.custom
  )?.navigationReference.find(({ template }) => template === channel.theme);

  const footerLinks = navigationReference?.footerNavigation
    ? filterAndFormatLinks(navigationReference.footerNavigation, placeId, venue, venuesPageSlug)
    : [];
  const navigationLinks = navigationReference?.mainNavigation
    ? filterAndFormatLinks(navigationReference.mainNavigation, placeId, venue, venuesPageSlug)
    : [];
  const subFooterLinks = navigationReference?.subFooterNavigation
    ? filterAndFormatLinks(navigationReference.subFooterNavigation, placeId, venue, venuesPageSlug)
    : [];

  const homepageTemplate = dynamicPlace.custom?.homepageTemplate?.find(({ template }) => {
    return template === channel.theme;
  });

  if (!homepageTemplate || !dynamicPlace.default || !dynamicPlace.custom) notFound();

  // check if the display name needs to include the brand
  const places = await getContent<Place>('place')
    .setDepth(0)
    .setTags([channel.channelId])
    .setWhere([['sys.channels', 'contains', channel.id]])
    .fetch(['fields.name', 'fields.custom.displayName']);
  const hasDuplicateTitles = hasDuplicates(
    places?.map(({ fields }) => ({ ...fields })) as Place[],
    (place) => place.custom?.displayName || place.name,
  );

  const seo = {
    title: homepageTemplate?.seo?.title
      ? { absolute: homepageTemplate.seo?.title }
      : dynamicPlace.custom?.displayName || dynamicPlace.name,
    description: homepageTemplate?.seo?.description || dynamicPlace.default?.summary,
    media: homepageTemplate?.seo?.media || dynamicPlace.default?.images?.primaryImage,
    alt: homepageTemplate?.seo?.title || dynamicPlace.name,
    hideEntry: homepageTemplate?.seo?.hideEntry,
  };

  const bookingTypes = dynamicPlace.bookingTypes
    ?.map((type) => {
      if (!type.availableOnVenues) return type;

      const venueData = type.availableOnVenues?.find((venue) => venue.id === place.id);

      if (!venueData) return null;

      const localisedDescription = venue
        ? prependToNonHttpsHrefs(type.venueLevelDescription?.content, `/${venuesPageSlug}/${venue}/`) ||
          type.description?.content
        : type.description?.content;

      return {
        ...type,
        description: { content: localisedDescription },
        additionalNotes: venueData?.additionalFields?.additionalNotes,
      };
    })
    .filter(Boolean) as VenueBookingType[];

  return {
    seo,
    placeId,
    schema: dynamicPlace.default,
    logo: fields.brandLogo,
    siteName: fields.name,
    displayName: !hasDuplicateTitles ? dynamicPlace.custom.displayName : dynamicPlace.name,
    socials: {
      ...(dynamicPlace.default.social || []),
      ...(dynamicPlace.default.contact || []),
    },
    address: [
      dynamicPlace.default.address.addressLine1,
      dynamicPlace.default.address.addressLine2,
      dynamicPlace.default.address.cityOrTown,
      dynamicPlace.default.address.country,
      dynamicPlace.default.address.county,
      dynamicPlace.default.address.postcode,
    ]
      .filter(Boolean)
      .join(', '),
    announcements: dynamicPlace.custom.venueAnnouncements?.filter(({ template }) => template === channel.theme),
    channelId: channel.channelId,
    channelApps: channel.apps,
    theme: channel.themeConfig,
    backgroundImage: fields.backgroundImage,
    backgroundImageOpacity: fields.backgroundImageOpacity,
    bookingTypes,
    navigationLinks,
    navigationActions: dynamicPlace.custom.navigationReference?.[0]?.actions,
    transitionName: channel.themeConfig?.animations,
    times: dynamicPlace.default.times,
    footer: {
      operatorLogo,
      operatorHref: org?.website
        ? org.website.startsWith('http')
          ? org.website
          : `https://${org.website}`
        : undefined,
      footerLinks,
      subFooterLinks,
      middleColumnContent: dynamicPlace?.custom?.venueFooter?.find(({ template }) => template === channel.theme)
        ?.middleColumn,
    },
    '404Page': {
      pageContent: dynamicPlace.custom.venue404Pages?.[0]?.pageContent,
      heroBanner: dynamicPlace.custom.venue404Pages?.[0]?.heroBanner,
    },
  };
}

/**
 * the structure of this is awful
 * BUT it matches the CMS structure so until we have unlimited depth in the CMS it makes sense to keep it this way
 * */
function filterAndFormatLinks(
  links: (RepeaterLink | NestedRepeaterLink)[],
  placeId: string,
  venue: string,
  venuesPageSlug: string,
): (RepeaterLink | NestedRepeaterLink)[] {
  if (!links?.length) return [];
  return (
    links
      ?.filter((item) => !item.showFor || item.showFor?.some((entry) => entry.id === placeId))
      .map((link) => ({
        ...link,
        url: !link.url?.includes('http') ? `/${venuesPageSlug}/${venue}${link.url}` : link.url,

        // secondary menu
        subMenu:
          'subMenu' in link && !link.subMenu
            ? null
            : 'subMenu' in link &&
              link.subMenu
                ?.filter((item) => !item.showFor || item.showFor?.some((entry) => entry.id === placeId))
                .map((item) => ({
                  ...item,
                  url: item.url && !item.url?.includes('http') ? `/${venuesPageSlug}/${venue}${item.url}` : item.url,

                  // tertiary menu
                  subMenu: !item.subMenu
                    ? null
                    : item.subMenu
                        ?.filter(
                          (subItem) => !subItem.showFor || subItem.showFor?.some((entry) => entry.id === placeId),
                        )
                        .map((subItem) => ({
                          ...subItem,
                          url:
                            subItem.url && !subItem.url?.includes('http')
                              ? `/${venuesPageSlug}/${venue}${subItem.url}`
                              : subItem.url,
                        })),
                })),
      })) || []
  );
}
